import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { TableChart, PictureAsPdf } from '@mui/icons-material';
import ThemeToggle from '../components/ThemeToggle';
import { exportUsersToExcel, exportUsersToPDF } from '../utils/exportUtils';

interface UserDetail {
  username: string;
  email: string | null;
  name: string | null;
  roles: string[];
  total_projects: number;
  tokens_remaining: number | null;
  tokens_used: number | null;
  created_at: string | null;
  average_score?: number | null;
  highest_score?: number | null;
  lowest_score?: number | null;
}

const AdminUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [users, setUsers] = useState<UserDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokensToAdd, setTokensToAdd] = useState<number>(0);

  useEffect(() => {
    let isMounted = true;
    const timeoutId = setTimeout(() => {
      if (isMounted && loading) {
        console.warn('Fetch users timeout - setting loading to false');
        setLoading(false);
        setError('İstek zaman aşımına uğradı. Lütfen tekrar deneyin.');
      }
    }, 30000); // 30 saniye timeout

    fetchUsers();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching users...');
      const data = await adminAPI.getUsers();
      console.log('Users fetched successfully:', data);
      setUsers(data || []);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Kullanıcılar yüklenemedi';
      setError(errorMessage);
      setUsers([]); // Set empty array on error
      
      if (err.response?.status === 403 || err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const handleAddTokens = async () => {
    if (!selectedUser || tokensToAdd <= 0) return;

    try {
      await adminAPI.updateUser(selectedUser.username, {
        tokens_to_add: tokensToAdd,
      });
      setShowTokenModal(false);
      setTokensToAdd(0);
      setSelectedUser(null);
      fetchUsers(); // Refresh list
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Token eklenemedi');
    }
  };

  const handleToggleTester = async (user: UserDetail) => {
    const isTester = user.roles.includes('tester');
    const newRoles = isTester
      ? user.roles.filter((r) => r !== 'tester')
      : [...user.roles.filter((r) => r !== 'tester'), 'tester'];
    try {
      await adminAPI.updateUser(user.username, { roles: newRoles });
      fetchUsers();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Rol güncellenemedi');
    }
  };

  const handleViewDetails = (username: string) => {
    navigate(`/admin/users/${username}`);
  };

  const filteredUsers = users.filter((user) =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 dark:border-gray-400 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 transition-colors">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Kullanıcı Yönetimi</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">Toplam {users.length} kullanıcı</p>
            </div>
            <div className="flex gap-3">
              {users.length > 0 && (
                <>
                  <button
                    onClick={() => exportUsersToExcel(users)}
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors font-medium"
                    title="Export to Excel"
                  >
                    <TableChart style={{ width: '18px', height: '18px' }} />
                    <span>Excel</span>
                  </button>
                  <button
                    onClick={() => exportUsersToPDF(users)}
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium"
                    title="Export to PDF"
                  >
                    <PictureAsPdf style={{ width: '18px', height: '18px' }} />
                    <span>PDF</span>
                  </button>
                </>
              )}
              <ThemeToggle size="sm" />
              <button
                onClick={() => navigate('/admin')}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Dashboard'a Dön
              </button>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Kullanıcı ara (isim, email, username)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 transition-colors"
          />
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/50 overflow-hidden transition-colors">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Kullanıcı
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Roller
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Projeler
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  İstatistikler
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tokenlar
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Kayıt Tarihi
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredUsers.map((user) => (
                <tr key={user.username} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{user.username}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{user.email || 'Email yok'}</div>
                      {user.name && <div className="text-xs text-gray-400 dark:text-gray-500">{user.name}</div>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-1">
                      {user.roles.map((role) => (
                        <span
                          key={role}
                          className={`px-2 py-1 text-xs rounded ${
                            role === 'admin'
                              ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
                              : role === 'tester'
                              ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300'
                          }`}
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {user.total_projects}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.total_projects > 0 ? (
                      <div className="text-sm space-y-0.5">
                        {user.average_score !== null && user.average_score !== undefined && (
                          <div className="text-gray-700 dark:text-gray-300 font-medium">
                            Ort: {user.average_score.toFixed(1)}/10
                          </div>
                        )}
                        {user.highest_score !== null && user.highest_score !== undefined && (
                          <div className="text-green-600 dark:text-green-400 text-xs">
                            En yüksek: {user.highest_score.toFixed(1)}
                          </div>
                        )}
                        {user.lowest_score !== null && user.lowest_score !== undefined && (
                          <div className="text-orange-600 dark:text-orange-400 text-xs">
                            En düşük: {user.lowest_score.toFixed(1)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-gray-500">Proje yok</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      <div className="text-green-600 dark:text-green-400 font-medium">
                        {user.tokens_remaining || 0} kalan
                      </div>
                      <div className="text-gray-500 dark:text-gray-400">{user.tokens_used || 0} kullanılmış</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {user.created_at
                      ? new Date(user.created_at).toLocaleDateString('tr-TR')
                      : 'Bilinmiyor'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleViewDetails(user.username)}
                      className="text-gray-700 dark:text-gray-300 hover:text-headings mr-3 transition-colors"
                    >
                      Detaylar
                    </button>
                    {!user.roles.includes('admin') && (
                      <button
                        onClick={() => handleToggleTester(user)}
                        className={`mr-3 text-xs px-2 py-1 rounded transition-colors ${
                          user.roles.includes('tester')
                            ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                      >
                        {user.roles.includes('tester') ? '✓ Test' : 'Test Yap'}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setSelectedUser(user);
                        setShowTokenModal(true);
                      }}
                      className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 transition-colors"
                    >
                      Token Ekle
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">Kullanıcı bulunamadı</p>
            </div>
          )}
        </div>
      </div>

      {/* Token Modal */}
      {showTokenModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 transition-colors">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 transition-colors">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Token Ekle</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              <span className="font-medium text-gray-900 dark:text-white">{selectedUser.username}</span> kullanıcısına token
              ekle
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Eklenecek Token Miktarı
              </label>
              <input
                type="number"
                min="1"
                value={tokensToAdd}
                onChange={(e) => setTokensToAdd(parseInt(e.target.value) || 0)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 transition-colors"
                placeholder="Örn: 10"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowTokenModal(false);
                  setTokensToAdd(0);
                  setSelectedUser(null);
                }}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleAddTokens}
                disabled={tokensToAdd <= 0}
                className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Ekle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
